import { useEffect, useState } from "react";
import { CASE_TASK_LABELS, formatTaskDuration, taskDurationMs } from "@/lib/caseTasks";
import { TASK_STATUS_LABELS, caseDisplayName, caseSecondaryName, taskStatus, type ManagerTask } from "@/lib/managerStats";
import type { TeamProfile } from "@/lib/useTeam";
import { Panel, ageTone, formatShortDateTime, personName } from "./managerUi";

const PAGE = 50;

const STATUS_STYLES = {
  open: "bg-amber-50 text-amber-800 ring-amber-200",
  completed: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  cancelled: "bg-gray-100 text-gray-600 ring-gray-200",
} as const;

const th = "px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap";
const td = "px-3 py-2 align-top text-sm text-gray-700";

export function ManagerTaskList({
  tasks,
  loading,
  now,
  profilesById,
  filtered,
  onOpenCase,
  onCancel,
}: {
  tasks: ManagerTask[];
  loading: boolean;
  now: number;
  profilesById: Map<string, TeamProfile>;
  filtered: boolean;
  onOpenCase: (caseId: string) => void;
  onCancel: (task: ManagerTask) => void;
}) {
  const [visible, setVisible] = useState(PAGE);
  useEffect(() => setVisible(PAGE), [tasks.length]);
  const shown = tasks.slice(0, visible);

  return (
    <Panel
      title="Détail des tâches"
      subtitle={loading ? "Chargement…" : `${tasks.length} tâche${tasks.length > 1 ? "s" : ""} · plus récentes d'abord`}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1100px] table-fixed">
          <colgroup>
            <col />
            <col className="w-[170px]" />
            <col className="w-[230px]" />
            <col className="w-[140px]" />
            <col className="w-[140px]" />
            <col className="w-[96px]" />
            <col className="w-[150px]" />
            <col className="w-[80px]" />
            <col className="w-[76px]" />
          </colgroup>
          <thead className="border-b border-gray-100 bg-gray-50/60">
            <tr>
              <th className={th}>Dossier</th>
              <th className={th}>Type</th>
              <th className={th}>Note</th>
              <th className={th}>Pour</th>
              <th className={th}>Créée</th>
              <th className={th}>Statut</th>
              <th className={th}>Cochée</th>
              <th className={`${th} text-right`}>Durée</th>
              <th className={th}>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && tasks.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-sm text-gray-400" role="status">
                  Chargement des tâches…
                </td>
              </tr>
            ) : tasks.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-10 text-center text-sm text-gray-500">
                  {filtered
                    ? "Aucune tâche ne correspond à ces filtres."
                    : "Aucune tâche pour l'instant. Créez-en une depuis le Kanban (clic droit sur un dossier)."}
                </td>
              </tr>
            ) : (
              shown.map((task) => {
                const status = taskStatus(task);
                const duration = taskDurationMs(task, now);
                const secondary = caseSecondaryName(task.case);
                return (
                  <tr key={task.id} className={status === "cancelled" ? "bg-gray-50/50" : "hover:bg-gray-50/60"}>
                    <td className={td}>
                      <button
                        type="button"
                        onClick={() => onOpenCase(task.case_id)}
                        className="block max-w-full truncate text-left font-medium text-indigo-700 hover:underline focus:outline-none focus-visible:underline"
                        title="Ouvrir la fiche"
                      >
                        {caseDisplayName(task.case)}
                      </button>
                      <div className="truncate text-xs text-gray-400">
                        {[task.case?.center, secondary].filter(Boolean).join(" · ") || " "}
                      </div>
                    </td>
                    <td className={`${td} text-gray-900`}>{CASE_TASK_LABELS[task.task_type] ?? task.task_type}</td>
                    <td className={td}>
                      {task.note ? (
                        <p className="line-clamp-2 text-gray-600" title={task.note}>
                          {task.note}
                        </p>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className={td}>
                      {task.assigned_to ? (
                        <>
                          <div className="truncate">{personName(profilesById, task.assigned_to)}</div>
                          <div className="text-xs text-gray-400">Personnelle</div>
                        </>
                      ) : (
                        <>
                          <div className="truncate">{task.assigned_center}</div>
                          <div className="text-xs text-gray-400">Équipe</div>
                        </>
                      )}
                    </td>
                    <td className={td}>
                      <div className="truncate">{personName(profilesById, task.created_by)}</div>
                      <div className="whitespace-nowrap text-xs text-gray-400">{formatShortDateTime(task.created_at)}</div>
                    </td>
                    <td className={td}>
                      <span
                        className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLES[status]}`}
                      >
                        {TASK_STATUS_LABELS[status]}
                      </span>
                    </td>
                    <td className={td}>
                      {status === "completed" ? (
                        <>
                          <div className="truncate text-gray-900">{personName(profilesById, task.completed_by)}</div>
                          <div className="whitespace-nowrap text-xs text-gray-400">{formatShortDateTime(task.completed_at)}</div>
                        </>
                      ) : status === "cancelled" ? (
                        <div className="text-xs text-gray-400">
                          Annulée par {personName(profilesById, task.cancelled_by)}
                          <br />
                          {formatShortDateTime(task.cancelled_at)}
                        </div>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td
                      className={`${td} whitespace-nowrap text-right tabular-nums ${status === "open" ? ageTone(duration) : "text-gray-900"}`}
                      title={status === "open" ? "Ouverte depuis" : status === "completed" ? "De la création à la coche" : undefined}
                    >
                      {status === "cancelled" ? <span className="text-gray-300">—</span> : formatTaskDuration(duration)}
                      {status === "open" && <div className="text-[11px] font-normal text-gray-400">en cours</div>}
                    </td>
                    <td className={`${td} text-right`}>
                      {status === "open" && (
                        <button
                          type="button"
                          onClick={() => onCancel(task)}
                          className="rounded px-1.5 py-0.5 text-xs font-medium text-gray-500 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                          aria-label={`Annuler la tâche ${CASE_TASK_LABELS[task.task_type] ?? ""} sur ${caseDisplayName(task.case)}`}
                        >
                          Annuler
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      {tasks.length > visible && (
        <div className="border-t border-gray-100 px-4 py-2.5 text-center">
          <button
            type="button"
            onClick={() => setVisible((count) => count + PAGE)}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-800 focus:outline-none focus-visible:underline"
          >
            Afficher {Math.min(PAGE, tasks.length - visible)} de plus ({tasks.length - visible} restantes)
          </button>
        </div>
      )}
    </Panel>
  );
}
